import { doc, getDoc, type DocumentData } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import type { AiAnalysisSettings, AiCategoryMaster } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * カード印刷の「AI 分類コード→日本語名」解決マップを構築する hook (card-print 専用)。
 *
 * 仕組み (ハードコードなし・完全に M2 マスタ駆動):
 *   1. 引数の settingsId 群 (地区の AI 設定 + 各投稿の aiAnalysis.settingsId) を distinct 化
 *   2. 各 M1 `aiAnalysisSettings/{id}` を読み、`categoryMasterIds[]` (後方互換 `categoryMasterId`) を集約
 *   3. 各 M2 `aiCategoryMasters/{id}` を読み、`rows[]` から code→表示名 (row.category) のマップを構築
 *
 * code キーは AI 出力/保存の揺れに両対応:
 *   - bare `categoryCd` (例 '01')
 *   - 結合形 `${majorCd}-${categoryCd}` (例 'D-01'、ECW categoryCodes の保存形)
 *
 * apps/* 間 deep import 禁止 (ESLint boundaries) のため、user app / posts-moderation の
 * 同等 hook は import せず minimum scope で複製。ただし posts-moderation 版と違い
 * **結合キーも登録** する (ECW の 'D-01' を解決するため、user 版 useAiCategoryMasters と同じ修正)。
 */

// module-level cache (settingsId / masterId 別、重複 fetch 抑制)。
// 注: getDoc ベースのため admin が M1/M2 を編集した直後は full reload まで stale (既存 user /
// posts-moderation hook と同方針)。card-print は編集フローに近いが、master 編集は別タブ/別画面で
// 行い印刷前に reload される運用前提。ライブ反映が必要になれば onSnapshot 版へ切替える。
// **取得失敗 (catch) 時は null をキャッシュしない** — 一過性のネットワーク失敗を恒久未解決に
// 固定しないため (次回再試行可能)。doc 不在 (exists=false) の null は安定値なのでキャッシュする。
const _settingsCache = new Map<string, AiAnalysisSettings | null>();
const _masterCache = new Map<string, AiCategoryMaster | null>();

async function loadSettings(settingsId: string): Promise<AiAnalysisSettings | null> {
  if (_settingsCache.has(settingsId)) return _settingsCache.get(settingsId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiAnalysisSettings', settingsId));
    const data = snap.exists()
      ? ({ ...(snap.data() as DocumentData), id: settingsId } as AiAnalysisSettings)
      : null;
    _settingsCache.set(settingsId, data);
    return data;
  } catch {
    return null; // catch はキャッシュせず再試行可能に
  }
}

async function loadMaster(masterId: string): Promise<AiCategoryMaster | null> {
  if (_masterCache.has(masterId)) return _masterCache.get(masterId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiCategoryMasters', masterId));
    const data = snap.exists()
      ? ({ ...(snap.data() as DocumentData), id: masterId } as AiCategoryMaster)
      : null;
    _masterCache.set(masterId, data);
    return data;
  } catch {
    return null; // catch はキャッシュせず再試行可能に
  }
}

export interface CardCategoryNamesResult {
  /** 分類コード (bare / 結合形) → 日本語表示名。 */
  codeToName: Record<string, string>;
  loading: boolean;
}

/**
 * settingsId 群から code→日本語名マップを構築。
 * settingsIds は毎レンダー新配列でも distinct キー文字列で安定化し再 fetch を抑える。
 */
export function useCardCategoryNames(
  settingsIds: readonly (string | null | undefined)[],
): CardCategoryNamesResult {
  const distinctKey = useMemo(
    () => [...new Set(settingsIds.filter((s): s is string => !!s))].sort().join(','),
    [settingsIds],
  );
  const [masters, setMasters] = useState<AiCategoryMaster[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ids = distinctKey ? distinctKey.split(',') : [];
    if (ids.length === 0) {
      setMasters([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const settingsList = await Promise.all(ids.map(loadSettings));
      const masterIds = new Set<string>();
      for (const s of settingsList) {
        const mids = s?.categoryMasterIds?.length
          ? s.categoryMasterIds
          : s?.categoryMasterId
            ? [s.categoryMasterId]
            : [];
        for (const mid of mids) if (mid) masterIds.add(mid);
      }
      const loaded = (await Promise.all([...masterIds].map(loadMaster))).filter(
        (m): m is AiCategoryMaster => !!m,
      );
      if (!cancelled) {
        setMasters(loaded);
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [distinctKey]);

  const codeToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of masters) {
      for (const r of m.rows ?? []) {
        if (!r.category) continue;
        if (r.categoryCd && !(r.categoryCd in map)) map[r.categoryCd] = r.category;
        if (r.majorCd && r.categoryCd) {
          const combined = `${r.majorCd}-${r.categoryCd}`;
          if (!(combined in map)) map[combined] = r.category;
        }
      }
    }
    return map;
  }, [masters]);

  return { codeToName, loading };
}
